import React, { useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import useStyles from './SettingsCard.style'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Switch from '@mui/material/Switch'
import IconButton from '@mui/material/IconButton'
import MicIcon from '@mui/icons-material/Mic'
import VideocamIcon from '@mui/icons-material/Videocam'
import EditIcon from '@mui/icons-material/Edit'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import { IoMdRefresh } from 'react-icons/io'
import { useTranslation } from 'react-i18next'
import styles from './Settings.module.scss'
import Cookies from 'universal-cookie'
import { getDeviceFriendlyName, setDeviceFriendlyName } from '../../utils/functions'
import { BrandedButton } from '../Shared/BrandedButton/BrandedButton.component'

export default function AVDeviceTable () {
  const classes = useStyles()
  const { t } = useTranslation()

  const [foundMicrophones, setFoundMicrophones] = useState(null)
  const [availableMicrophones, setAvailableMicrophones] = useState(null)
  const [foundWebcams, setFoundWebcams] = useState(null)
  const [availableWebcams, setAvailableWebcams] = useState(null)
  const [disabledVideoDevices, setDisabledVideoDevices] = useState([])
  const [disabledAudioDevices, setDisabledAudioDevices] = useState([])
  const [defaultVideoDevice, setDefaultVideoDevice] = useState(null)
  const [defaultMicrophoneDevice, setDefaultMicrophoneDevice] = useState(null)
  const [defaultStethoscopeDevice, setDefaultStethoscopeDevice] = useState(null)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [editingDevice, setEditingDevice] = useState({ id: null, type: null, originalName: '' })
  const [renameValue, setRenameValue] = useState('')

  useEffect(() => {
    getDevices()

    const cookies = new Cookies()
    const videoDevices = cookies.get('disabledVideoDevices')
    const audioDevices = cookies.get('disabledAudioDevices')
    const defaultVideo = cookies.get('videoinput')
    const defaultMicrophone = cookies.get('audioinput')
    const defaultStethoscope = cookies.get('stethoscopeDevice')

    if (videoDevices) {
      setDisabledVideoDevices(videoDevices)
    }

    if (audioDevices) {
      setDisabledAudioDevices(audioDevices)
    }

    if (defaultVideo) {
      setDefaultVideoDevice(defaultVideo)
    }

    if (defaultMicrophone) {
      setDefaultMicrophoneDevice(defaultMicrophone)
    }

    if (defaultStethoscope) {
      setDefaultStethoscopeDevice(defaultStethoscope)
    }
  }, [])

  const checkVideoFeed = async (deviceId) => {
    const localAvailableWebcams = { ...availableWebcams }
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: deviceId } }
    })

    if (!stream) {
      localAvailableWebcams[deviceId] = false
    } else {
      const result = await stream
        .getVideoTracks()
        .some((track) => track.enabled && track.readyState === 'live')
      localAvailableWebcams[deviceId] = result
      stream.getTracks().forEach((track) => track.stop())
    }

    setAvailableWebcams(localAvailableWebcams)
  }

  const checkAudioFeed = async (deviceId) => {
    const localAvailableMicrophones = { ...availableMicrophones }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: deviceId } }
    })

    if (!stream) {
      localAvailableMicrophones[deviceId] = false
    } else {
      const result = await stream
        .getAudioTracks()
        .some((track) => track.enabled && track.readyState === 'live')
      localAvailableMicrophones[deviceId] = result
      stream.getTracks().forEach((track) => track.stop())
    }

    setAvailableMicrophones(localAvailableMicrophones)
  }

  async function getDevices () {
    const foundMicrophones = {}
    const availableMicrophones = {}
    const foundWebcams = {}
    const availableWebcams = {}

    let audioStream, videoStream

    try {
      // Request audio permissions first
      audioStream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (error) {
      // Audio permission error
    }

    try {
      // Request video permissions next
      videoStream = await navigator.mediaDevices.getUserMedia({ video: true })
    } catch (error) {
      // Video permission error
    }

    // Stop all tracks after permissions are granted
    if (audioStream) {
      audioStream.getTracks().forEach((track) => track.stop())
    }
    if (videoStream) {
      videoStream.getTracks().forEach((track) => track.stop())
    }

    const devices = await navigator.mediaDevices.enumerateDevices()

    const seenGroupIds = { videoinput: new Set(), audioinput: new Set() }

    devices.forEach((device, i) => {
      if (device.deviceId === 'default' || device.deviceId === 'communications') return
      if (device.groupId && seenGroupIds[device.kind]?.has(device.groupId)) return
      if (device.groupId) seenGroupIds[device.kind]?.add(device.groupId)

      if (device.kind === 'videoinput') {
        foundWebcams[device.deviceId] = device.label || `camera ${i + 1}`
        availableWebcams[device.deviceId] = null
      } else if (device.kind === 'audioinput') {
        foundMicrophones[device.deviceId] =
          device.label || `microphone ${i + 1}`
        availableMicrophones[device.deviceId] = null
      }
    })

    setFoundMicrophones(foundMicrophones)
    setFoundWebcams(foundWebcams)
    setAvailableMicrophones(availableMicrophones)
    setAvailableWebcams(availableWebcams)
  }

  const toggleDevice = (deviceType, deviceId) => {
    const cookies = new Cookies()
    let updatedDevices

    if (deviceType === 'video') {
      updatedDevices = disabledVideoDevices.includes(deviceId)
        ? disabledVideoDevices.filter((id) => id !== deviceId)
        : [...disabledVideoDevices, deviceId]

      setDisabledVideoDevices(updatedDevices)
      cookies.set('disabledVideoDevices', JSON.stringify(updatedDevices), {
        maxAge: 1000 * 60 * 60 * 24 * 365 // 365 days
      })
    } else if (deviceType === 'audio') {
      updatedDevices = disabledAudioDevices.includes(deviceId)
        ? disabledAudioDevices.filter((id) => id !== deviceId)
        : [...disabledAudioDevices, deviceId]

      setDisabledAudioDevices(updatedDevices)
      cookies.set('disabledAudioDevices', JSON.stringify(updatedDevices), {
        maxAge: 1000 * 60 * 60 * 24 * 365 // 365 days
      })
    }
  }

  const setDefaultDevice = (deviceType, deviceId) => {
    const cookies = new Cookies()

    if (deviceType === 'video') {
      const isAlreadyDefault = defaultVideoDevice === deviceId
      setDefaultVideoDevice(isAlreadyDefault ? null : deviceId)
      if (isAlreadyDefault) {
        cookies.remove('videoinput')
      } else {
        cookies.set('videoinput', deviceId, {
          maxAge: 1000 * 60 * 60 * 24 * 365 // 365 days
        })
      }
    } else if (deviceType === 'microphone') {
      const isAlreadyDefault = defaultMicrophoneDevice === deviceId
      setDefaultMicrophoneDevice(isAlreadyDefault ? null : deviceId)
      if (isAlreadyDefault) {
        cookies.remove('audioinput')
      } else {
        cookies.set('audioinput', deviceId, {
          maxAge: 1000 * 60 * 60 * 24 * 365 // 365 days
        })
      }
    } else if (deviceType === 'stethoscope') {
      const isAlreadyDefault = defaultStethoscopeDevice === deviceId
      setDefaultStethoscopeDevice(isAlreadyDefault ? null : deviceId)
      if (isAlreadyDefault) {
        cookies.remove('stethoscopeDevice')
      } else {
        cookies.set('stethoscopeDevice', deviceId, {
          maxAge: 1000 * 60 * 60 * 24 * 365 // 365 days
        })
      }
    }
  }

  const handleOpenRenameDialog = (deviceId, deviceType, originalName) => {
    const cookies = new Cookies()
    const currentFriendlyName = getDeviceFriendlyName(deviceId, originalName, cookies)
    setEditingDevice({ id: deviceId, type: deviceType, originalName })
    setRenameValue(currentFriendlyName)
    setRenameDialogOpen(true)
  }

  const handleCloseRenameDialog = () => {
    setRenameDialogOpen(false)
    setEditingDevice({ id: null, type: null, originalName: '' })
    setRenameValue('')
  }

  const handleSaveRename = () => {
    if (editingDevice.id) {
      const cookies = new Cookies()
      setDeviceFriendlyName(editingDevice.id, renameValue, cookies)
      // Refresh device names to show updated friendly names
      getDevices()
      handleCloseRenameDialog()
    }
  }

  return (
    <div className={styles.AVDeviceTable}>
      <div style={{ marginBottom: 10 }}>
        <BrandedButton
          onClick={getDevices}
          buttonLabel="Refresh Devices"
          startIcon={<IoMdRefresh />}
        />
      </div>
      <TableContainer>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="left">
                <b>{t('Device')}</b>
              </TableCell>
              <TableCell align="center">
                <b>{t('Enabled')}</b>
              </TableCell>
              <TableCell align="center">
                <b>{t('Test')}</b>
              </TableCell>
              <TableCell align="center">
                <b>{t('Actions')}</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {foundMicrophones && availableMicrophones
              ? Object.keys(foundMicrophones).map((item) => {
                const isDisabled = disabledAudioDevices.includes(item)
                return (
                  <TableRow
                    key={item}
                    className={`${isDisabled ? styles.disabledRow : styles.enabledRow}`}
                  >
                      <TableCell component="th" scope="row">
                        <div className={classes.tableCell}>
                          <MicIcon className={isDisabled ? styles.disabledIcon : styles.enabledIcon} />
                          <div className={styles.deviceNameContainer}>
                            <div className={styles.deviceNameRow}>
                              <span className={`${styles.deviceName} ${isDisabled ? styles.disabledText : ''}`}>
                                {getDeviceFriendlyName(item, foundMicrophones[item], new Cookies())}
                              </span>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenRenameDialog(item, 'audio', foundMicrophones[item])}
                                className={styles.editButton}
                                title={t('Rename Device')}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </div>
                            {(defaultMicrophoneDevice === item || defaultStethoscopeDevice === item) && (
                              <div className={styles.defaultBadgesContainer}>
                                {defaultMicrophoneDevice === item && (
                                  <span className={styles.defaultBadge}>
                                    <StarIcon className={styles.starIcon} />
                                    {t('Default Microphone')}
                                  </span>
                                )}
                                {defaultStethoscopeDevice === item && (
                                  <span className={styles.defaultBadge}>
                                    <StarIcon className={styles.starIcon} />
                                    {t('Default Stethoscope')}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={!isDisabled}
                          onChange={() => toggleDevice('audio', item)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {availableMicrophones[item] === null
                          ? (
                            <Button
                              className={styles.buttonColor}
                              variant="outlined"
                              color="primary"
                              onClick={() => checkAudioFeed(item)}
                              size="small"
                            >
                              {t('Test')}
                            </Button>
                            )
                          : availableMicrophones[item]
                            ? (
                              <span className={`${styles.enabledBadge} ${styles.clickableBadge}`} onClick={() => checkAudioFeed(item)}>
                                <CheckCircleIcon className={styles.statusIcon} />
                                {t('Passed')}
                              </span>
                              )
                            : (
                              <span className={`${styles.disabledBadge} ${styles.clickableBadge}`} onClick={() => checkAudioFeed(item)}>
                                <CancelIcon className={styles.statusIcon} />
                                {t('Failed')}
                              </span>
                              )}
                      </TableCell>
                      <TableCell align="center">
                        <div className={styles.actionsContainer}>
                          {!isDisabled && (
                            <div className={styles.secondaryActions}>
                              <Button
                                className={`${styles.buttonColor} ${defaultMicrophoneDevice === item ? styles.defaultButton : ''}`}
                                variant={defaultMicrophoneDevice === item ? 'contained' : 'outlined'}
                                color="primary"
                                onClick={() => setDefaultDevice('microphone', item)}
                                startIcon={defaultMicrophoneDevice === item ? <StarIcon /> : <StarBorderIcon />}
                                size="small"
                              >
                                {t('Default Mic')}
                              </Button>
                              <Button
                                className={`${styles.buttonColor} ${defaultStethoscopeDevice === item ? styles.defaultButton : ''}`}
                                variant={defaultStethoscopeDevice === item ? 'contained' : 'outlined'}
                                color="primary"
                                onClick={() => setDefaultDevice('stethoscope', item)}
                                startIcon={defaultStethoscopeDevice === item ? <StarIcon /> : <StarBorderIcon />}
                                size="small"
                              >
                                {t('Stethoscope')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                )
              })
              : null}

            {foundWebcams && availableWebcams
              ? Object.keys(foundWebcams).map((item) => {
                const isDisabled = disabledVideoDevices.includes(item)
                return (
                  <TableRow
                    key={item}
                    className={`${isDisabled ? styles.disabledRow : styles.enabledRow}`}
                  >
                      <TableCell component="th" scope="row">
                        <div className={classes.tableCell}>
                          <VideocamIcon className={isDisabled ? styles.disabledIcon : styles.enabledIcon} />
                          <div className={styles.deviceNameContainer}>
                            <div className={styles.deviceNameRow}>
                              <span className={`${styles.deviceName} ${isDisabled ? styles.disabledText : ''}`}>
                                {getDeviceFriendlyName(item, foundWebcams[item], new Cookies())}
                              </span>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenRenameDialog(item, 'video', foundWebcams[item])}
                                className={styles.editButton}
                                title={t('Rename Device')}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </div>
                            {defaultVideoDevice === item && (
                              <div className={styles.defaultBadgesContainer}>
                                <span className={styles.defaultBadge}>
                                  <StarIcon className={styles.starIcon} />
                                  {t('Default Camera')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={!isDisabled}
                          onChange={() => toggleDevice('video', item)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {availableWebcams[item] === null
                          ? (
                            <Button
                              className={styles.buttonColor}
                              variant="outlined"
                              color="primary"
                              onClick={() => checkVideoFeed(item)}
                              size="small"
                            >
                              {t('Test')}
                            </Button>
                            )
                          : availableWebcams[item]
                            ? (
                              <span className={`${styles.enabledBadge} ${styles.clickableBadge}`} onClick={() => checkVideoFeed(item)}>
                                <CheckCircleIcon className={styles.statusIcon} />
                                {t('Passed')}
                              </span>
                              )
                            : (
                              <span className={`${styles.disabledBadge} ${styles.clickableBadge}`} onClick={() => checkVideoFeed(item)}>
                                <CancelIcon className={styles.statusIcon} />
                                {t('Failed')}
                              </span>
                              )}
                      </TableCell>
                      <TableCell align="center">
                        <div className={styles.actionsContainer}>
                          {!isDisabled && (
                            <div className={styles.secondaryActions}>
                              <Button
                                className={`${styles.buttonColor} ${defaultVideoDevice === item ? styles.defaultButton : ''}`}
                                variant={defaultVideoDevice === item ? 'contained' : 'outlined'}
                                color="primary"
                                onClick={() => setDefaultDevice('video', item)}
                                startIcon={defaultVideoDevice === item ? <StarIcon /> : <StarBorderIcon />}
                                size="small"
                              >
                                {t('Default Camera')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                )
              })
              : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={renameDialogOpen} onClose={handleCloseRenameDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('Rename Device')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('Device Name')}
            fullWidth
            variant="outlined"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder={editingDevice.originalName}
            helperText={t('Enter a friendly name for this device')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRenameDialog}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleSaveRename}
            variant="contained"
            color="primary"
            className={styles.buttonColor}
            sx={{ my: 0 }}
          >
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
